<template>
  <div
    class="home-container"
    :class="{ 'home-container--hero-mode': viewMode === 'hero' }"
  >
    <div class="home-banner">
      <div class="banner-content">
        <div class="banner-left">
          <img
            v-if="isImageLoaded"
            :src="imageUrl + organizationName + '.jpg'"
            alt="Organization Logo"
            class="banner-logo"
            @error="handleImageError"
          />
          <a-typography-title v-else :level="2" class="banner-title">
            Organization Summary
          </a-typography-title>
        </div>
        <div class="banner-right">
          <a-button
            type="primary"
            @click="toggleArchivedView"
            class="archive-toggle-button"
            size="large"
            :loading="loading"
          >
            <template #icon>
              <archive-outlined v-if="!isArchived" />
              <eye-outlined v-else />
            </template>
            {{ isArchived ? 'Show Active Devices' : 'Show Archived Devices' }}
          </a-button>
        </div>
      </div>
    </div>
    <div class="cards-container p-lg-4 p-xl-4 p-md-3 p-sm-4 p-2">
      <div class="cards-wrapper px-lg-4 px-xl-4 px-md-3 px-sm-4 px-2">
        <KpiStrip :kpis="fleetKpis" class="mb-3" />

        <div class="view-switcher-row mb-3">
          <a-radio-group v-model:value="viewMode" button-style="solid" size="small">
            <a-radio-button value="detailed">
              Detailed
              <pushpin-filled
                v-if="preferredViewMode === 'detailed'"
                class="preferred-pin"
              />
            </a-radio-button>
            <a-radio-button value="compact">
              Compact
              <pushpin-filled
                v-if="preferredViewMode === 'compact'"
                class="preferred-pin"
              />
            </a-radio-button>
            <a-radio-button value="table">
              Table
              <pushpin-filled
                v-if="preferredViewMode === 'table'"
                class="preferred-pin"
              />
            </a-radio-button>
            <a-radio-button value="hero">
              Hero split
              <pushpin-filled
                v-if="preferredViewMode === 'hero'"
                class="preferred-pin"
              />
            </a-radio-button>
          </a-radio-group>
          <a-tooltip
            :title="
              preferredViewMode === viewMode
                ? 'This is your default view'
                : 'Set as default view'
            "
          >
            <a-button
              size="small"
              type="text"
              class="ml-2 pin-btn"
              :disabled="preferredViewMode === viewMode"
              @click="saveViewPreference"
            >
              <template #icon><pushpin-outlined /></template>
            </a-button>
          </a-tooltip>
        </div>

        <a-row
          v-if="viewMode === 'compact'"
          class="pb-4"
          :gutter="[16, 16]"
        >
          <a-col
            v-for="device in sortedDevices"
            :key="device.Serial_number"
            :xs="24"
            :sm="12"
            :md="8"
            :lg="6"
            :xl="6"
            :xxl="4"
          >
            <work-station-card-compact
              :device="device"
              :active-devices="activeDevices"
              :id-to-task-name-mapping="idToTaskNameMapping"
              :active-devices-status="devicesStats"
              :value-story-config-map="valueStoryConfigMap"
            />
          </a-col>
        </a-row>

        <work-station-table
          v-else-if="viewMode === 'table'"
          class="pb-4"
          :devices="sortedDevices"
          :active-devices="activeDevices"
          :active-devices-status="devicesStats"
          :id-to-task-name-mapping="idToTaskNameMapping"
          :value-story-config-map="valueStoryConfigMap"
        />

        <work-station-hero-split
          v-else-if="viewMode === 'hero'"
          :devices="sortedDevices"
          :active-devices="activeDevices"
          :active-devices-status="devicesStats"
          :id-to-task-name-mapping="idToTaskNameMapping"
          :value-story-config-map="valueStoryConfigMap"
        />

        <a-row v-else class="pb-4" :gutter="[24, 24]">
          <a-col
            v-for="device in sortedDevices"
            :key="device.Serial_number"
            :xs="24"
            :sm="24"
            :md="24"
            :lg="12"
            :xl="8"
            :xxl="8"
          >
            <work-station-card
              :device="device"
              :active-devices="activeDevices"
              :id-to-task-name-mapping="idToTaskNameMapping"
              :id-to-device-serial-no-mapping="idToDeviceSerialNoMapping"
              :active-devices-settings="activeDevicesSettings"
              :active-devices-status="devicesStats"
              :value-story-config-map="valueStoryConfigMap"
              @device-archived="handleDeviceArchived"
              @device-unarchived="handleDeviceUnarchived"
            />
          </a-col>
        </a-row>
        <div
          v-if="sortedDevices.length === 0 && !loading"
          style="height: 70vh"
          class="no-active-device"
        >
          <laptop-outlined class="text-secondary" />
          <a-typography-title :level="3" class="mt-2 text-secondary">
            {{
              isArchived
                ? 'No Archived Device(s) Found'
                : 'No Active Device(s) Found'
            }}
          </a-typography-title>
        </div>

        <!-- Skeleton Loader -->
        <div v-if="loading" class="skeleton-container">
          <a-row :gutter="[24, 24]">
            <a-col
              v-for="n in 6"
              :key="n"
              :xs="24"
              :sm="24"
              :md="24"
              :lg="12"
              :xl="8"
              :xxl="8"
            >
              <a-card class="skeleton-card">
                <a-skeleton active :paragraph="{ rows: 4 }" />
              </a-card>
            </a-col>
          </a-row>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import DeviceService from '@/services/device.js';
import TaskService from '@/services/tasks.js';
import {
  ArchiveOutlined,
  EyeOutlined,
  LaptopOutlined,
  PushpinFilled,
  PushpinOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { dateFormat } from 'src/config/date-format-config.js';
import { inject, ref, watch } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { useActiveDevices } from '../RecordData/composables/useActiveDevices';
import { useDeviceSettings } from '../RecordData/composables/useDeviceSettings';
import { roughCostAvoided } from 'src/utils/valueStory/enrichSteps';
import WorkStationCard from './WorkStationCard.vue';
import WorkStationCardCompact from './WorkStationCardCompact.vue';
import WorkStationTable from './WorkStationTable.vue';
import WorkStationHeroSplit from './WorkStationHeroSplit.vue';
import KpiStrip from './KpiStrip.vue';
dayjs.extend(relativeTime);

export default {
  components: {
    WorkStationCard,
    WorkStationCardCompact,
    WorkStationTable,
    WorkStationHeroSplit,
    KpiStrip,
    LaptopOutlined,
    ArchiveOutlined,
    EyeOutlined,
    PushpinFilled,
    PushpinOutlined,
  },
  setup() {
    const toast = inject('toast');
    const deviceRef = ref({});
    const { activeDevices: curentActiveDevices } = useActiveDevices();

    const { devicesSettingsDict, devicesStatusDict } = useDeviceSettings(toast);

    watch(
      () => curentActiveDevices.value,
      (dict) => {
        if (!Object.keys(dict).length) return;
        deviceRef.value = {};
        Object.entries(dict).forEach(([, value]) => {
          deviceRef.value[value?.Serial_number] = value;
        });
      }
    );

    return {
      // activeDevices,
      // activeDevicesList,
      devicesSettingsDict,
      devicesStatusDict,
      activeDevices: deviceRef,
      toast,
    };
  },
  data() {
    const stored =
      typeof window !== 'undefined'
        ? window.localStorage?.getItem('dashboard_view_mode')
        : null;
    const allowed = ['detailed', 'compact', 'table', 'hero'];
    const preferred = allowed.includes(stored) ? stored : 'detailed';
    return {
      viewMode: preferred,
      preferredViewMode: preferred,
      isArchived: false,
      loading: false,
      deviceList: [],
      archivedDeviceList: [],
      // activeDevices: {},
      // isPolled: false,
      // activeDevicesPolling: null,
      tasksList: [],
      idToTaskNameMapping: {},
      idToDeviceSerialNoMapping: {},
      activeDevicesSettings: [],
      deviceStatsPolling: null,
      devicesStats: {},
      valueStoryConfigMap: {},
      valueStoryConfigsRequestedFor: new Set(),
      isImageLoaded: true,
      imageUrl:
        'https://retrocausal-video-thumbnails.s3.us-east-2.amazonaws.com/logos/',
    };
  },

  computed: {
    ...mapGetters([
      'organization',
      'organizationName',
      'devices',
      'startedDevice',
      'hasValueStoryAccess',
    ]),
    sortedDevices() {
      const currentList = this.isArchived
        ? this.archivedDeviceList
        : this.deviceList;
      if (!currentList.length) return [];
      const [active, starred, inactive] = this.sortDevices(
        currentList,
        this.sortDevicesCallback
      );
      currentList.map((device) => {
        if (!device.last_Active.endsWith('ago')) {
          if (device.last_Active.endsWith('UTC'))
            device.last_Active = dayjs(
              new Date(device.last_Active).toISOString()
            ).fromNow();
        }
        return device;
      });
      this.sortActiveDevicesByDisplayName(active);
      return [...active, ...starred, ...inactive];
    },

    fleetKpis() {
      const list = this.sortedDevices;
      const stats = this.devicesStats;
      const active = this.activeDevices;
      let total = 0;
      let online = 0;
      let guidance = 0;
      let recording = 0;
      let cyclesToday = 0;
      let badToday = 0;
      let costAvoidedToday = 0;
      for (const d of list) {
        total += 1;
        const settings = active[d.Serial_number];
        if (!settings?.active) continue;
        online += 1;
        if (settings.isInferenceRunning) guidance += 1;
        if (settings.record_shift) recording += 1;
        const s = stats[`${d.Serial_number}${settings.Task}`];
        if (s) {
          cyclesToday += Number(s.number_of_cycles) || 0;
          badToday += Number(s.bad_cycles) || 0;
        }
        if (settings.isInferenceRunning && s?.bad_cycles) {
          const cfg = this.valueStoryConfigMap[settings.Task];
          if (cfg) {
            costAvoidedToday += roughCostAvoided(
              cfg,
              Number(s.bad_cycles) || 0
            );
          }
        }
      }
      return {
        total,
        online,
        offline: total - online,
        guidance,
        recording,
        cyclesToday,
        badToday,
        badPct: cyclesToday > 0 ? (badToday / cyclesToday) * 100 : 0,
        costAvoidedToday,
      };
    },
  },

  watch: {
    // Subscriptions load after mount, so access can turn on later.
    hasValueStoryAccess(hasAccess) {
      if (hasAccess) this.refreshValueStoryConfigs();
    },
    startedDevice: {
      handler(val) {
        if (val) {
          this.deviceList = this.deviceList.map((data) => {
            if (val.id == data.id) {
              return { ...data, starred: val.starred };
            }
            return { ...data };
          });

          this.starredDevices(null);
        }
      },
      deep: true,
    },
    devices: {
      handler(val) {
        this.deviceList = [...val];
      },
      deep: true,
    },
    deviceList(list) {
      if (!list.length) return;
      this.idToDeviceSerialNoMapping = this.getIdToAttributeMapOfList(
        list,
        'Serial_number'
      );
    },
    tasksList(tasks) {
      if (!tasks.length) return;
      this.idToTaskNameMapping = this.getIdToAttributeMapOfList(
        tasks,
        'taskName'
      );
    },
    activeDevices: {
      handler(dict) {
        if (dict && Object.keys(dict)?.length) {
          if (!this.deviceStatsPolling) this.setDeviceStatsInterval();
        } else if (this.deviceStatsPolling) {
          clearInterval(this.deviceStatsPolling);
          this.deviceStatsPolling = null;
        }
        this.refreshValueStoryConfigs();
      },
      deep: true,
    },
  },

  async created() {
    await this.checkPermissionsState();
    this.getAllDevices();
    this.getTasksList();
    this.getActiveDevicesStats();
  },

  beforeUnmount() {
    if (this.deviceStatsPolling) clearInterval(this.deviceStatsPolling);
  },

  methods: {
    ...mapActions(['getOrgDevices', 'starredDevices']),

    saveViewPreference() {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem('dashboard_view_mode', this.viewMode);
      } catch (e) {
        this.toast?.error?.('Could not save your view preference.');
        return;
      }
      this.preferredViewMode = this.viewMode;
      this.toast?.success?.(`Set ${this.labelForView(this.viewMode)} as your default view.`);
    },

    labelForView(mode) {
      return (
        { detailed: 'Detailed', compact: 'Compact', table: 'Table', hero: 'Hero split' }[mode] ||
        mode
      );
    },
    async toggleArchivedView() {
      this.loading = true;
      const newStatus = !this.isArchived;

      try {
        if (newStatus && this.archivedDeviceList.length === 0) {
          // Only fetch archived devices if we haven't loaded them yet
          await this.getOrgDevices({
            orgName: this.organization,
            isArchived: true,
          });
          // Store the archived devices in our local state
          this.archivedDeviceList = [...this.devices];
        } else if (!newStatus && this.deviceList.length === 0) {
          // Only fetch active devices if we haven't loaded them yet
          await this.getOrgDevices({
            orgName: this.organization,
            isArchived: false,
          });
          this.deviceList = [...this.devices];
        }

        this.isArchived = newStatus;
      } catch (error) {
        console.error('Error toggling archived view:', error);
        this.$message.error('Failed to load devices. Please try again.');
      } finally {
        this.loading = false;
      }
    },
    async checkPermissionsState() {
      try {
        const microphoneStatus = await navigator.permissions.query({
          name: 'microphone',
        });
        const cameraStatus = await navigator.permissions.query({
          name: 'camera',
        });

        if (
          microphoneStatus.state === 'denied' ||
          microphoneStatus.state === 'prompt' ||
          cameraStatus.state === 'denied' ||
          cameraStatus.state === 'prompt'
        ) {
          this.requestPermissions();
        } else {
          // Permissions are already granted
        }
      } catch (error) {
        console.log('Error checking media permissions:', error);
      }
    },
    async requestPermissions() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        // Stop the stream to avoid using resources unnecessarily
        stream.getTracks().forEach((track) => track.stop());
        await this.checkPermissionsState(); // Update permissions after requesting
      } catch (err) {
        console.log('Permissions denied:', err);
      }
    },
    async refreshValueStoryConfigs() {
      if (!this.hasValueStoryAccess) return;
      const active = this.activeDevices || {};
      const taskIds = new Set();
      for (const key of Object.keys(active)) {
        const settings = active[key];
        if (!settings?.active) continue;
        if (!settings.isInferenceRunning) continue;
        const tid = settings.Task;
        if (!tid) continue;
        if (this.valueStoryConfigsRequestedFor.has(String(tid))) continue;
        taskIds.add(tid);
      }
      if (!taskIds.size) return;
      const ids = Array.from(taskIds);
      ids.forEach((id) =>
        this.valueStoryConfigsRequestedFor.add(String(id))
      );
      const [error, data] = await TaskService.fetchValueStoryConfigs(ids);
      if (error) {
        // On error, allow retry next tick.
        ids.forEach((id) =>
          this.valueStoryConfigsRequestedFor.delete(String(id))
        );
        return;
      }
      const results = data?.results || {};
      this.valueStoryConfigMap = {
        ...this.valueStoryConfigMap,
        ...results,
      };
    },

    formatDateWithCurrentTime(date) {
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      };
      const formattedDate = new Intl.DateTimeFormat('en-US', options).format(
        date
      );
      return formattedDate.replace(' PM', '').replace(' GMT+5', '');
    },

    sortDevicesCallback(serialNumber, starred) {
      if (this.activeDevices && this.activeDevices[serialNumber]) return 0;
      if (starred === true) return 1;
      else return 2;
    },

    sortDevices(devices, checkCondition) {
      return devices.reduce(
        (acc, e) => {
          acc[checkCondition(e.Serial_number, e.starred)].push(e);
          return acc;
        },
        [[], [], []]
      );
    },

    getIdToAttributeMapOfList(list, attributeName) {
      return list.reduce((res, el) => {
        res[el.id] = el[attributeName];
        return res;
      }, {});
    },

    async getAllDevices() {
      this.loading = true;
      if (this.devices.length == 0) {
        await this.getOrgDevices(this.organization);
      } else {
        this.deviceList = [...this.devices];
        // this.startLoadingMoreDevices()
      }
      this.loading = false;
    },

    startLoadingMoreDevices() {
      this.intervalId = setInterval(() => {
        this.loadMoreDevices();
      }, 2000);
    },

    async loadMoreDevices() {
      if (this.deviceList.length >= this.devices.length) {
        return;
      }

      this.isLoadingMore = true;

      // Simulate async data fetch
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const nextBatch = this.devices.slice(
        this.deviceList.length,
        this.deviceList.length + 10
      );
      this.deviceList = this.deviceList.concat(nextBatch);
      this.isLoadingMore = false;
    },

    async getTasksList() {
      const [error, data] = await TaskService.fetchTasks(false);
      if (error) return;
      this.tasksList = data;
    },

    async getActiveDevicesStats() {
      const currentDate = `${dayjs(new Date().toISOString()).format(
        dateFormat
      )}`;
      const [error, data] = await DeviceService.getDeviecesStats(false, {
        current_date: currentDate,
      });
      if (error || !data.length) return;

      const stats = data.reduce((res, device) => {
        res[`${device.serial_number}${device.task_id}`] = { ...device.stats };
        return res;
      }, {});
      this.devicesStats = stats;
    },

    setDeviceStatsInterval() {
      this.deviceStatsPolling = setInterval(() => {
        this.getActiveDevicesStats();
      }, 10000);
    },

    sortActiveDevicesByDisplayName(active) {
      active.sort((a, b) => {
        const firstDevice = a.display_name?.toLowerCase();
        const secondDevice = b.display_name?.toLowerCase();
        if (firstDevice === null && secondDevice === null) return 0;
        if (firstDevice === null) return 1;
        if (secondDevice === null) return -1;
        if (firstDevice < secondDevice) return -1;
        if (firstDevice > secondDevice) return 1;
        return 0;
      });
    },

    handleImageError() {
      this.isImageLoaded = false;
    },

    handleDeviceArchived(archivedDevice) {
      // Remove the archived device from the active device list
      this.deviceList = this.deviceList.filter(
        (device) => device.id !== archivedDevice.id
      );

      // Add the device to the archived devices list (prevent duplication)
      const existingIndex = this.archivedDeviceList.findIndex(
        (device) => device.id === archivedDevice.id
      );
      if (existingIndex === -1) {
        this.archivedDeviceList.push(archivedDevice);
      }

      console.log('Device archived:', archivedDevice);
      this.$message.success(
        `${
          archivedDevice.display_name || archivedDevice.Serial_number
        } has been moved to archived devices.`
      );
    },

    handleDeviceUnarchived(unarchivedDevice) {
      // Remove the unarchived device from the archived device list
      this.archivedDeviceList = this.archivedDeviceList.filter(
        (device) => device.id !== unarchivedDevice.id
      );

      // Add the device to the active devices list (prevent duplication)
      const existingIndex = this.deviceList.findIndex(
        (device) => device.id === unarchivedDevice.id
      );
      if (existingIndex === -1) {
        this.deviceList.push(unarchivedDevice);
      }

      console.log('Device unarchived:', unarchivedDevice);
      this.$message.success(
        `${
          unarchivedDevice.display_name || unarchivedDevice.Serial_number
        } has been moved to active devices.`
      );
    },
  },
};
</script>
<style>
.home-container {
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
  /* padding-top: 60px; Adjust for fixed header */
}

/* Hero-mode fills the ContentV2 layout (which is already 92vh, scroll auto)
   so the split card can flex-fill without triggering the parent scrollbar. */
.home-container--hero-mode {
  height: 100%;
  overflow: hidden;
}
.home-container--hero-mode .home-banner {
  flex-shrink: 0;
}
.home-container--hero-mode .cards-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.home-container--hero-mode .cards-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.home-banner {
  width: 100%;
  height: 15vh;
  background: linear-gradient(135deg, #1890ff, #6f42c1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

.banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  max-width: 1400px;
  margin: 0 auto;
}

.banner-left {
  display: flex;
  align-items: center;
}

.banner-logo {
  width: 100%;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 20px;
  /* box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1); */
}

.banner-title {
  color: white;
  margin-bottom: 0;
}

.banner-right {
  display: flex;
  align-items: center;
}

.archive-toggle-button {
  background: linear-gradient(135deg, #1890ff, #6f42c1);
  border: none;
  border-radius: 25px;
  box-shadow: 0 6px 20px rgba(24, 144, 255, 0.4);
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-width: 200px;
}

.archive-toggle-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}

.archive-toggle-button:hover {
  background: linear-gradient(135deg, #6f42c1, #1890ff);
  box-shadow: 0 8px 25px rgba(24, 144, 255, 0.5);
  transform: translateY(-2px);
}

.archive-toggle-button:hover::before {
  left: 100%;
}

.archive-toggle-button:active {
  transform: translateY(0);
  box-shadow: 0 4px 15px rgba(24, 144, 255, 0.4);
}

.skeleton-container {
  padding: 24px;
}

.skeleton-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.skeleton-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.cards-container {
  width: 100%;
  transform: translateY(-35px);
  position: relative;
  z-index: 99;
}

.cards-wrapper {
  width: 100%;
}

.no-active-device {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.no-active-device > .anticon-laptop {
  font-size: 3em;
}

.view-switcher-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.preferred-pin {
  font-size: 10px;
  color: #faad14;
  margin-left: 4px;
  vertical-align: middle;
}
.pin-btn:disabled {
  color: #faad14 !important;
  opacity: 1;
}

/* Make every card in a row stretch to match the tallest — so the grid
   stays uniform even when guidance cards have Top-missed and idle/offline
   cards don't. */
.cards-wrapper .ant-row {
  align-items: stretch;
}
.cards-wrapper .ant-col {
  display: flex;
  flex-direction: column;
}

@media only screen and (max-width: 576px) {
  .cards-container {
    transform: translateY(-40px);
  }
}
</style>
